// eslint-disable-next-line
// @ts-nocheck
import React, { useEffect, useState } from "react";
import { getAccount } from "@wagmi/core";
import { useAccount } from "wagmi";
import io from "socket.io-client";
import DOMPurify from "dompurify";

import { config } from "../Web3Modal";

export interface Submit {
  transaction: any;
  signature: string;
  walletAddress: string;
  protocol_name?: string;
}

export interface Message {
  agent: "ai" | "human" | "ui";
  message: string;
  component?: string;
  data?: any;
}

export enum ResponseType {
  MESSAGE = "message",
  SWAP = "swap",
  TRANSACTION_STATUS = "transaction_status",
  SIGN_AND_SUBMIT_TRANSACTION = "sign_and_submit_tx",
  SIGN_MESSAGE = "sign_message",
  SELECT_OPTION = "select_option",
}

function onSocketConnection({
  loading,
  setLoading,
  setMessages,
  setSocket,
  setChatId,
  setError,
  accessToken,
}: any) {
  const socketInstance = io(process.env.REACT_APP_SOCKET_ENDPOINT as string, {
    query: { accessToken },
  });

  socketInstance.on("connect", () => {
    setSocket(socketInstance);
    setChatId(socketInstance.id as string);
    setLoading(false);
  });

  socketInstance.on("data", async (data: any) => {
    if (data?.message) {
      !loading && setLoading(true);
      const ai_message = data.message;

      setMessages((m: Message[]) => [
        ...m,
        { agent: "ai", message: ai_message },
      ]);
      setLoading(false);
    } else if (data?.render_component) {
      setLoading(false);
      setMessages((m: Message[]) => [
        ...m,
        {
          agent: "ui",
          message: "",
          component: data.render_component,
          data: data.render_data,
        },
      ]);
    }
  });

  socketInstance.on("message_end", async () => {
    // we are ready for more interactions with the user
    setLoading(false);
  });

  socketInstance.on("error", async (data: any) => {
    setLoading(false);
    console.error("error", data);
    if (data?.code === 429) {
      if (data?.message.includes("disconnecting")) {
        setError(
          "Due to the amount of messages sent in a very short period of time, you have been disconnected. Please reload the page and try again"
        );
        return setLoading(true);
      } else
        return setError(
          "Please wait a few seconds before sending another message network may be congested. If the problem persist please contact support."
        );
    }
    return setError(
      "Error: Something went wrong, please try again or reload the page. If the problem persist please contact support"
    );
  });

  socketInstance.on("disconnect", (data: any) => {
    console.log("disconnected", data);
  });
}

export function useChat() {
  const [socket, setSocket] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [input, setInput] = useState<string>("");
  const [chatId, setChatId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string>("");
  const { address: walletAddress } = useAccount();

  useEffect(() => {
    if (!accessToken) return;

    if (chatId) return;
    if (!socket) {
      try {
        onSocketConnection({
          loading,
          setLoading,
          setMessages,
          setSocket,
          setChatId,
          setError,
          setSubmitted,
          accessToken,
        });
      } catch (e) {
        console.log("error", e);
      }
    }

    return () => {
      if (socket && chatId) {
        setChatId("");
        socket && socket.disconnect();
        setSocket(null);
      }
    };
  }, [accessToken]);

  if (socket && submitted) {
    socket.disconnect();
  }

  async function appendMessage(_message?: string) {
    const message: string = _message || input;
    const cleanMessage = DOMPurify.sanitize(message);
    if (!cleanMessage) return;
    if (!message) return;
    if (!walletAddress)
      return setError("Please connect your wallet to interact.");
    if (chatId === "")
      return setError(
        "Chat not created yet, please wait for the spinner in the chat to finish loading."
      );
    const { chainId } = getAccount(config);

    if (chainId !== 2222) {
      return setError("Please switch to the Kava network to interact.");
    }
    try {
      // send message
      setMessages((m) => [
        ...m,
        { agent: "human", message: cleanMessage },
      ]);
      setLoading(true);
      setInput("");
      const account = getAccount(config);
      socket.emit("data", {
        message:
          cleanMessage +
          " " +
          (account.chain?.name ? `chain=${account.chain?.name}` : ""),
        accessToken: accessToken,
        walletAddress,
      });
    } catch (e: any) {
      console.error(e);
      setError(e.response?.data?.error ?? e.message);
      setLoading(false);
    }
  }

  async function sendMessageHidden(message: string) {
    const cleanMessage = DOMPurify.sanitize(message);

    if (!cleanMessage) return;
    if (!walletAddress)
      return setError("Please connect your wallet to interact.");
    if (chatId === "")
      return setError(
        "Chat not created yet, please wait for the spinner in the chat to finish loading."
      );

    try {
      // send message
      socket.emit("data", {
        message: cleanMessage,
        accessToken: accessToken,
        walletAddress,
      });
      setLoading(true);
    } catch (e: any) {
      console.error(e);
      setError(e.response?.data?.error ?? e.message);
      setLoading(false);
    }
  }

  async function sendUserSignature(signature: string) {
    const cleanSignature = DOMPurify.sanitize(signature);
    if (!cleanSignature) return;
    if (!walletAddress)
      return setError("Please connect your wallet to interact.");

    try {
      socket.emit("data", {
        message: `Signature: ${cleanSignature}.`,
        accessToken: accessToken,
        walletAddress,
      });
    } catch (e: any) {
      console.error(e);
      setError(e.response?.data?.error ?? e.message);
    }
  }

  const resetChat = async () => {
    setMessages([]);
    setChatId("");
    setSubmitted(false);
    // TODO find a way to reset the chat/ connection
    // await createChat();
    window.location.reload();
  };


  const submit = async ({
    transaction,
    signature,
    walletAddress,
    protocol_name,
  }: Submit) => {
    try {
      socket.emit("submit", {
        transaction,
        signature,
        walletAddress,
        protocol_name,
        accessToken: accessToken
      });
    } catch (e) {
      console.error(e);
    }
  };


  return {
    messages,
    setMessages,
    appendMessage,
    loading,
    setLoading,
    input,
    setInput,
    chatId,
    setSubmitted,
    sendUserSignature,
    resetChat,
    submitted,
    accessToken,
    setAccessToken,
    error,
    setError,
    sendMessageHidden,
    submit
  };
}
